import React, { useState } from 'react'
import styled, { css } from 'styled-components'

import { useLocation } from 'react-router-dom'
import { useStore } from '../zustandStore'
import { shallow } from 'zustand/shallow'

import { ARROW_DOWN, ARROW_RIGHT } from '../unicodeChars'
import { FULL_GAME_MODE, IL_MODE, BINGO_MODE } from '../utils'

function SettingsMenu(props) {
  const {
    publicAppSettings,
    setPublicAppSettings,
    isFullGame,
    isBingo,
    isIl,
    eventMode,
  } = useStore(state => ({
    publicAppSettings: state.publicAppSettings,
    setPublicAppSettings: state.setPublicAppSettings,
    isFullGame: state.event.isFullGame,
    isBingo: state.event.isBingo,
    isIl: state.event.isIl,
    eventMode: state.event.mode,
  }))

  const { pathname } = useLocation()

  const handleClickDecimals = e => {
    setPublicAppSettings({ displayedDecimals: Number(e.target.value) })
  }

  const handleClickMovementAnimations = e => {
    setPublicAppSettings({ movementAnimations: e.target.checked })
  }

  const handleClickBlinkingAnimations = e => {
    setPublicAppSettings({ blinkingAnimations: e.target.checked })
  }

  const handleClickSubmissionAnimations = e => {
    setPublicAppSettings({ submissionAnimations: e.target.checked })
  }

  const handleClickBingoPointsAnimations = e => {
    setPublicAppSettings({ bingoPointsAnimations: e.target.checked })
  }

  const [isOpen, setIsOpen] = useState(false)
  // const [isOpen, setIsOpen] = useState(true)
  const toggleOpen = () => setIsOpen(!isOpen)

  const isDetailedBoard = pathname.split('/').includes('detailed')

  return (
    <StyleWrapper className='settings-menu-wrapper' eventMode={eventMode}>
      <div className='header' onClick={toggleOpen}>
        <div className='arrow'>
          {isOpen ?  ARROW_DOWN : ARROW_RIGHT}
        </div>
        <div className='title'>
          {isIl ? 'Settings' : 'Animation Settings'}
        </div> 
      </div>
      {isOpen && (
        <div className='body'>
          {isIl && (
            <div className={`decimals-wrapper ${isDetailedBoard ? '' : 'disabled'}`}>
              <div className='sub-header'>Decimals</div>

              <label htmlFor="seconds" className={publicAppSettings.displayedDecimals == 0 ? 'selected' : null}>
                <input
                  type="radio"
                  id="seconds"
                  name="decimals"
                  value={0}
                  checked={publicAppSettings.displayedDecimals === 0}
                  onChange={handleClickDecimals}
                />
                Seconds
              </label>

              <label htmlFor="tenths" className={publicAppSettings.displayedDecimals == 1 ? 'selected' : null}>
                <input
                  type="radio"
                  id="tenths"
                  name="decimals"
                  value={1}
                  checked={publicAppSettings.displayedDecimals === 1}
                  onChange={handleClickDecimals}
                />
                Tenths
              </label>

              <label htmlFor="ms" className={publicAppSettings.displayedDecimals == 3 ? 'selected' : null}>
                <input
                  type="radio"
                  id="ms"
                  name="decimals"
                  value={3}
                  checked={publicAppSettings.displayedDecimals === 3}
                  onChange={handleClickDecimals}
                />
                Milliseconds
              </label>
            </div>
          )}

          <div className="checkboxes-wrapper">
            {isIl && (
              <div className="checkboxes-title sub-header">Animations</div>
            )}

            <div className='first-checkbox' title={isFullGame ? 'Rank change' : 'Rank change, sorting change'}>
              <label htmlFor="movementAnimations">
                Movement
                <input
                  type="checkbox"
                  id="movementAnimations"
                  name="movementAnimations"
                  checked={publicAppSettings.movementAnimations}
                  onChange={handleClickMovementAnimations}
                />
              </label>
            </div>

            <div className='second-checkbox' title={isBingo ? 'Pulsing red dots' : 'New PBs, pulsing red dots' }>
              <label htmlFor="blinkingAnimations">
                Blinking
                <input
                  type="checkbox"
                  id="blinkingAnimations"
                  name="blinkingAnimations"
                  checked={publicAppSettings.blinkingAnimations}
                  onChange={handleClickBlinkingAnimations}
                />
              </label>
            </div>

            {isBingo ? (
              <div className='third-checkbox' title='Scrolling numbers'>
                <label htmlFor="bingoPointsAnimations">
                  Bingo ticks
                  <input
                    type="checkbox"
                    id="bingoPointsAnimations"
                    name="bingoPointsAnimations"
                    checked={publicAppSettings.bingoPointsAnimations}
                    onChange={handleClickBingoPointsAnimations}
                  />
                </label>
              </div>
            ) : (
              <div className='third-checkbox'>
                <label htmlFor="submissionAnimations">
                  Submissions feed
                  <input
                    type="checkbox"
                    id="submissionAnimations"
                    name="submissionAnimations"
                    checked={publicAppSettings.submissionAnimations}
                    onChange={handleClickSubmissionAnimations}
                  />
                </label>
              </div>
            )}
          </div>
        </div>
      )}
    </StyleWrapper>
  )
}

export default SettingsMenu

const StyleWrapper = styled.div`
  border: 2px solid #aaa;
  border-radius: 3px;
  font-size: 1.2em;

  // stops the highlighting from clicking the menu too fast
  user-select: none;

  @media only screen and (max-width: ${() => `${window.phoneCutoffSizePx}px`}) {
    font-size: 1.5em;
  }

  width: 90%;

  .header {
    padding: 0.3em 0.3em 0.3em 0.3em;
    font-size: 1.1em;
    cursor: pointer;
    display: grid;
    grid-template-columns: 15% 70% 15%;
    background-color: var(--panel-title-obs-color-lighter);
    .arrow {
      grid-column: 1;
      grid-row: 1;
    }
    .title {
      grid-column: 2;
      grid-row: 1;
      text-align: center;
    }
    &:hover {
      background-color: var(--blue-hover);
    }
    &:active {
      background-color: var(--blue-active);
    }
  }

  .body {
    padding: 0 0.5em 0.2em 0.5em;
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: space-between;
  }

  .checkboxes-wrapper {
    ${props => props.eventMode !== IL_MODE ? css`
      width: 100%;
    ` : css`
      display: grid;
      grid-template-columns: min-content 1fr;
      grid-template-rows: 1lh 1lh 1lh;
    `}
  }

  label { 
    cursor: pointer;
    margin-top: auto;
    margin-bottom: auto;
    white-space: nowrap;
    &.selected {
      /* color: #ccc; */
      pointer-events: none;
    }
    &:hover {
      color: var(--blue-text);
    }
    width: 100%;
    display: inline-block;
   }
  input[type='radio'], input[type='checkbox'] {
    cursor: pointer;
    margin-top: auto;
    margin-bottom: auto;
  }

  .sub-header {
    text-decoration: underline;
    /* width: 100%;
    text-align: center; */
  }

  .decimals-wrapper {
    display: flex;
    flex-direction: column;
    grid-row-start: 1;
    grid-row-end: none;
    grid-column: 1;
    transition-property: opacity;
    transition-duration: 600ms;
    &.disabled {
      opacity: 0.35;
      cursor: not-allowed;
      pointer-events: none;
    }
  }

  .checkboxes-title {
    text-align: right;
    grid-column: 2;
    grid-row: 1;
  }

  .checkboxes-title, .first-checkbox, .second-checkbox, .third-checkbox {
    ${props => props.eventMode !== IL_MODE ? css`
      text-align: center;
    ` : css`
      grid-column: 2;
      text-align: right;
    `}
    width: 100%;
  }
  .first-checkbox {
    grid-row: 2;
  }
  .second-checkbox {
    grid-row: 3;
  }
  .third-checkbox {
    grid-row: 4;
  }

`